import React, { useState, useEffect } from 'react';
import { useAlert } from '../context/AlertContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Plus, Trash2, Edit, Search, User, ChevronRight } from 'lucide-react';
import { Table, Button, InputGroup, Form, Card, Row, Col, Image } from 'react-bootstrap';
import { formatPhoneBrazilian } from '../utils/formatters';
import './ProfessionalList.css';


interface Professional {
    id: number;
    name: string;
    phone: string;
    bio: string;
    photo?: string;
}

const ProfessionalList = () => {
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const [professionals, setProfessionals] = useState<Professional[]>([]);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchProfessionals();
    }, []);

    const fetchProfessionals = async () => {
        try {
            const response = await api.get('/professionals/');
            setProfessionals(response.data);
        } catch (error) {
            console.error('Error fetching professionals:', error);
        }
    };

    const handleDelete = (id: number) => {
        showAlert('Tem certeza que deseja excluir?', {
            title: 'Confirmação',
            confirmable: true,
            confirmText: 'Excluir',
            confirmVariant: 'btn-primary',
            onConfirm: async () => {
                try {
                    await api.delete(`/professionals/${id}/`);
                    fetchProfessionals();
                } catch (error) {
                    console.error('Error deleting professional:', error);
                }
            }
        });
    };

    const filteredProfessionals = professionals.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.phone.includes(searchTerm)
    );

    return (
        <Card className="shadow-sm border-0">
            <Card.Body>
                <Row className="mb-4 align-items-center">
                    <Col>
                        <h3 className="mb-0">Profissionais</h3>
                    </Col>
                    <Col xs="auto">
                        <Button variant="success" onClick={() => navigate('/professionals/new')} className="d-flex align-items-center gap-2">
                            <Plus size={18} /> Novo Profissional
                        </Button>
                    </Col>
                </Row>

                <InputGroup className="mb-4">
                    <InputGroup.Text className="bg-white border-end-0">
                        <Search size={18} className="text-muted" />
                    </InputGroup.Text>
                    <Form.Control
                        placeholder="Pesquisar por nome ou telefone..."
                        value={searchTerm}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                        className="border-start-0 ps-0"
                    />
                </InputGroup>

                {/* Desktop: Tabela */}
                <div className="custom-table-container d-none d-md-block">
                    <Table hover className="custom-table mb-0">
                        <thead>
                            <tr>
                                <th style={{ width: '80px' }}>Foto</th>
                                <th>Nome</th>
                                <th>Telefone</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredProfessionals.map((prof) => (
                                <tr
                                    key={prof.id}
                                    onClick={() => navigate(`/professionals/${prof.id}`)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td>
                                        {prof.photo ? (
                                            <Image
                                                src={`${prof.photo}`}
                                                alt={prof.name}
                                                roundedCircle
                                                style={{
                                                    width: '50px',
                                                    height: '50px',
                                                    objectFit: 'cover',
                                                    border: '2px solid #dee2e6'
                                                }}
                                            />
                                        ) : (
                                            <div
                                                className="d-flex align-items-center justify-content-center bg-light rounded-circle"
                                                style={{
                                                    width: '50px',
                                                    height: '50px',
                                                    border: '2px solid #dee2e6'
                                                }}
                                            >
                                                <User size={24} className="text-muted" />
                                            </div>
                                        )}
                                    </td>
                                    <td className="fw-medium align-middle">{prof.name}</td>
                                    <td className="align-middle">{formatPhoneBrazilian(prof.phone)}</td>
                                    <td className="text-end align-middle">
                                        <Button
                                            variant="link"
                                            className="text-secondary p-1 me-2"
                                            onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation();
                                                navigate(`/professionals/${prof.id}`);
                                            }}
                                        >
                                            <Edit size={18} />
                                        </Button>
                                        <Button
                                            variant="link"
                                            className="text-secondary p-1"
                                            onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation();
                                                handleDelete(prof.id);
                                            }}
                                        >
                                            <Trash2 size={18} />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            {filteredProfessionals.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="text-center py-4 text-muted">
                                        Nenhum profissional encontrado.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>

                {/* Mobile: Lista de Cards */}
                <div className="professional-list-mobile d-md-none">
                    {filteredProfessionals.length > 0 ? (
                        filteredProfessionals.map((prof) => (
                            <div key={prof.id} className="professional-card-mobile">
                                <div 
                                    className="professional-card-mobile-content"
                                    onClick={() => navigate(`/professionals/${prof.id}`)}
                                >
                                    {prof.photo ? (
                                        <Image
                                            src={`${prof.photo}`}
                                            alt={prof.name}
                                            roundedCircle
                                            width={48}
                                            height={48}
                                            style={{ objectFit: 'cover' }}
                                            className="professional-card-mobile-photo"
                                        />
                                    ) : (
                                        <div className="professional-card-mobile-photo-placeholder">
                                            <User size={28} className="text-secondary" />
                                        </div>
                                    )}
                                    <div className="professional-card-mobile-info">
                                        <span className="professional-card-mobile-name">{prof.name}</span>
                                        <span className="professional-card-mobile-phone">{formatPhoneBrazilian(prof.phone)}</span>
                                    </div>
                                    <ChevronRight size={20} className="text-muted" />
                                </div>
                                <div className="professional-card-mobile-actions">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="professional-card-mobile-btn"
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            navigate(`/professionals/${prof.id}`);
                                        }}
                                    >
                                        <Edit size={16} /> Editar
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="professional-card-mobile-btn"
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            handleDelete(prof.id);
                                        }}
                                    >
                                        <Trash2 size={16} /> Excluir
                                    </Button>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-4 text-muted">
                            Nenhum profissional encontrado.
                        </div>
                    )}
                </div>
            </Card.Body>
        </Card>
    );
};

export default ProfessionalList;
